import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PermissaoEnum } from '../../../../shared/enums/permissao.enum';
import { IbptOrigemExecucaoEnum } from '../../../../shared/enums/motor-fiscal/ibpt-origem-execucao.enum';
import { IbptResultadoExecucaoEnum } from '../../../../shared/enums/motor-fiscal/ibpt-resultado-execucao.enum';
import { IbptMotivoFalhaExecucaoEnum } from '../../../../shared/enums/motor-fiscal/ibpt-motivo-falha-execucao.enum';
import { IbptTipoLogExecucaoEnum } from '../../../../shared/enums/motor-fiscal/ibpt-tipo-log-execucao.enum';
import { TemPermissaoDirective } from '../../../../shared/directives/habilita-por-permissao.directive';
import { MapaUfIbptComponent } from '../mapa-uf-ibpt/mapa-uf-ibpt.component';
import { DetalheExecucaoIbptResponse } from '../../../../shared/services/responses/motor-fiscal/detalhe-execucao-ibpt.response';
import { ExecucaoIbptStruct } from '../../../../shared/services/structs/motor-fiscal/execucao-ibpt.struct';
import { IbptExecucaoTentativa } from '../../../../shared/services/models/motor-fiscal/ibpt-execucao-tentativa.model';
import { UnidadeFederativa } from '../../../../shared/services/models/usuario/unidade-federativa.model';

export interface DetalheExecucaoIbptDialogData {
  // Horários já convertidos para o fuso local pela tela principal.
  detalheExecucao: DetalheExecucaoIbptResponse;
  detalhe: string;
  duracao: string;
  // Lookup de UF já carregado pela tela principal (vazio se ainda não carregou): converte o id do arquivo em sigla.
  listaUnidadeFederativa: UnidadeFederativa[];
}

/**
 * Diálogo "Detalhe da execução" da Tabela IBPT, só para exibir: resumo, tentativas (API) ou arquivos (planilha) e
 * os logs gravados pela execução.
 */
@Component({
  selector: 'app-detalhe-execucao-ibpt-dialog',
  imports: [
    CommonModule,
    RouterModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatTooltipModule,
    TemPermissaoDirective,
    MapaUfIbptComponent,
  ],
  templateUrl: './detalhe-execucao-ibpt-dialog.component.html',
  styleUrl: './detalhe-execucao-ibpt-dialog.component.css',
})
export class DetalheExecucaoIbptDialogComponent {
  public origemExecucaoEnum = IbptOrigemExecucaoEnum;
  public resultadoExecucaoEnum = IbptResultadoExecucaoEnum;
  public tipoLogExecucaoEnum = IbptTipoLogExecucaoEnum;
  public permissaoEnum = PermissaoEnum;

  public execucao: ExecucaoIbptStruct;
  // Planilha: resultado por UF no mapa, pela mesma regra da conclusão da importação.
  public situacaoPorUf: Record<string, 'concluida' | 'parcial' | 'falhou'> = {};
  public displayedColumnsTentativa: string[] = ['numero', 'datetimeInicio', 'datetimeFim', 'resultado'];
  public displayedColumnsArquivo: string[] = [
    'nomeArquivo',
    'idUnidadeFederativa',
    'quantidadeLinhasValidas',
    'quantidadeLinhasRejeitadas',
  ];
  public displayedColumnsLog: string[] = [
    'idIbptExecucaoLog',
    'tipo',
    'funcao',
    'mensagem',
    'datetimeInclusao',
    'acao',
  ];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DetalheExecucaoIbptDialogData,
    private dialogRef: MatDialogRef<DetalheExecucaoIbptDialogComponent>
  ) {
    this.execucao = data.detalheExecucao.execucao!;

    // Concluída = nenhuma linha rejeitada; parcial = válidas e rejeitadas; falhou = nenhuma válida.
    // Sem o lookup, o mapa fica sem cores.
    this.execucao.listaArquivo.forEach(arquivo => {
      const sigla = data.listaUnidadeFederativa.find(
        uf => uf.idUnidadeFederativa === arquivo.idUnidadeFederativa
      )?.uf;
      if (!sigla) {
        return;
      }

      if (arquivo.quantidadeLinhasValidas === 0) {
        this.situacaoPorUf[sigla] = 'falhou';
      } else if (arquivo.quantidadeLinhasRejeitadas === 0) {
        this.situacaoPorUf[sigla] = 'concluida';
      } else {
        this.situacaoPorUf[sigla] = 'parcial';
      }
    });
  }

  // Sigla da UF pelo id do lookup; "—" sem o lookup.
  public siglaUf(idUnidadeFederativa: number): string {
    return (
      this.data.listaUnidadeFederativa.find(uf => uf.idUnidadeFederativa === idUnidadeFederativa)?.uf ?? '—'
    );
  }

  public fechar(): void {
    this.dialogRef.close();
  }

  public descricaoResultado(idResultado: IbptResultadoExecucaoEnum): string {
    switch (idResultado) {
      case IbptResultadoExecucaoEnum.EmAndamento:
        return 'Em andamento';
      case IbptResultadoExecucaoEnum.Sucesso:
        return 'Sucesso';
      case IbptResultadoExecucaoEnum.SemAlteracao:
        return 'Sem alteração';
      case IbptResultadoExecucaoEnum.Cancelada:
        return 'Cancelada';
      default:
        return 'Falha';
    }
  }

  public iconeResultado(idResultado: IbptResultadoExecucaoEnum): string {
    switch (idResultado) {
      case IbptResultadoExecucaoEnum.EmAndamento:
        return 'sync';
      case IbptResultadoExecucaoEnum.Sucesso:
        return 'check';
      case IbptResultadoExecucaoEnum.SemAlteracao:
        return 'remove';
      case IbptResultadoExecucaoEnum.Cancelada:
        return 'block';
      default:
        return 'close';
    }
  }

  // Mesmos textos do motivo usados na tela principal.
  public descricaoResultadoTentativa(tentativa: IbptExecucaoTentativa): string {
    if (tentativa.isSucesso === null) {
      return 'Em andamento';
    }

    if (tentativa.isSucesso) {
      return 'Sucesso';
    }

    // A tentativa encerrada por cancelamento não tem motivo de falha; a que falha sempre tem.
    if (tentativa.idIbptMotivoFalhaExecucao === null) {
      return 'Cancelada';
    }

    switch (tentativa.idIbptMotivoFalhaExecucao) {
      case IbptMotivoFalhaExecucaoEnum.SemRespostaApi:
        return 'Falha: tempo esgotado';
      case IbptMotivoFalhaExecucaoEnum.CredenciaisRecusadas:
        return 'Falha: credenciais recusadas pelo IBPT';
      case IbptMotivoFalhaExecucaoEnum.ItensComErroAcimaLimite:
        return 'Falha: itens com erro acima do limite';
      case IbptMotivoFalhaExecucaoEnum.ErroGravacao:
        return 'Falha: erro na gravação';
      default:
        return 'Falha: erro inesperado';
    }
  }

  public descricaoTipoLog(idTipo: IbptTipoLogExecucaoEnum): string {
    switch (idTipo) {
      case IbptTipoLogExecucaoEnum.Erro:
        return 'Erro';
      case IbptTipoLogExecucaoEnum.Sucesso:
        return 'Sucesso';
      default:
        return 'Aviso';
    }
  }
}
